/* eslint-disable no-else-return */

import type React from 'react';

import { useState, useMemo, useEffect, useCallback } from 'react';
import { Modal, Button, Typography, Row, Col, Calendar, Select } from 'antd';
import moment, { Moment } from 'moment';
import dayjs, { Dayjs } from 'dayjs';
import { CalendarOutlined, CloseOutlined } from '@ant-design/icons';
import styles from './styles.module.less';

const { Title, Text } = Typography;

const BUSINESS_HOURS_CONFIG = {
  START: { hour: 8, minute: 0 },
  END: { hour: 16, minute: 45 },
  LUNCH_TIME: { hour: 12, minute: 0 },
} as const;

const BASE_TIMEZONE = 'America/New_York';

const TIME_INTERVALS = ['00', '15', '30', '45'] as const;
const MODAL_WIDTH = 350;
const CALENDAR_MAX_WIDTH = 280;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSubmit: (sendAt: string) => void;
  initialSendAt?: string;
}

interface QuickOption {
  label: string;
  value: Dayjs;
}

interface TimeState {
  date: string;
  time: string;
  selectedDate: Dayjs | null;
}

const formatDateForDisplay = (date: Dayjs): string => {
  return date.local().format('MMMM D, hh:mm A');
};

const useTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getTimeOfDayLabel = (date: Dayjs): 'morning' | 'afternoon' | 'evening' | 'night' => {
  const hour = date.hour();
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

const buildLabel = (prefix: string, date: Dayjs) => {
  const tod = getTimeOfDayLabel(date);
  return `${prefix} ${tod}`;
};

const getFirstWeekdayOfMonth = (value: moment.Moment): moment.Moment => {
  const today = moment().startOf('day');
  let date = value.clone().startOf('month');

  while (date.day() === 0 || date.day() === 6 || date.isBefore(today, 'day')) {
    date = date.add(1, 'day');
  }

  return date;
};

const getAdjustedBusinessHours = (timeZone: string, date: Dayjs) => {
  const base = BUSINESS_HOURS_CONFIG;

  const convert = ({ hour, minute }: { hour: number; minute: number }) => {
    const nyTime = dayjs.tz(date.format('YYYY-MM-DD'), BASE_TIMEZONE).hour(hour).minute(minute).second(0);

    const localTime = nyTime.clone().tz(timeZone);

    return {
      hour: localTime.hour(),
      minute: localTime.minute(),
    };
  };

  return {
    START: convert(base.START),
    END: convert(base.END),
    LUNCH_TIME: convert(base.LUNCH_TIME),
  };
};

const getBusinessPhase = (
  nowMinutes: number,
  {
    START,
    LUNCH_TIME: LUNCH,
    END,
  }: {
    START: { hour: number; minute: number };
    LUNCH_TIME: { hour: number; minute: number };
    END: { hour: number; minute: number };
  },
): 'before' | 'morning' | 'afternoon' | 'after' => {
  const toMinutes = (t: { hour: number; minute: number }) => t.hour * 60 + t.minute;

  const start = toMinutes(START);
  const lunch = toMinutes(LUNCH);
  const end = toMinutes(END);

  const crossesMidnight = end <= start;

  const normalize = (m: number) => {
    if (!crossesMidnight) return m;
    return m < end ? m + 24 * 60 : m;
  };

  const now = normalize(nowMinutes);

  if (now < normalize(start)) return 'before';
  if (now < normalize(lunch)) return 'morning';
  if (now < normalize(end)) return 'afternoon';
  return 'after';
};

const useQuickOptions = (): QuickOption[] => {
  const userTimeZone = useTimeZone();
  const now = dayjs().tz(userTimeZone);
  const nowMinutes = now.hour() * 60 + now.minute();
  const today = now.startOf('day');
  const businessHours = getAdjustedBusinessHours(userTimeZone, now);
  const phase = getBusinessPhase(nowMinutes, businessHours);

  const { START, LUNCH_TIME: LUNCH } = businessHours;

  const createOption = (label: string, date: Dayjs): QuickOption => ({
    label,
    value: date.utc(),
  });

  const nextBusinessDay = (from: Dayjs, offset = 1): Dayjs => {
    let candidate = from.clone().add(offset, 'day').startOf('day');
    while (candidate.day() === 0 || candidate.day() === 6) {
      candidate = candidate.add(1, 'day');
    }
    return candidate;
  };

  const options: QuickOption[] = [];
  const isWeekend = today.day() === 0 || today.day() === 6;
  const isFriday = today.day() === 5;
  const isThursday = today.day() === 4;

  const isFridayAfterHours = isFriday && phase === 'after';
  const isThursdayAfterHours = isThursday && phase === 'after';

  const tomorrow = nextBusinessDay(today, 1);
  const dayAfterTomorrow = nextBusinessDay(today, 2);

  if (isWeekend || isFridayAfterHours) {
    const monday = nextBusinessDay(today, 1);
    const tuesday = nextBusinessDay(monday, 1);
    return [
      createOption(
        buildLabel('Monday', monday.hour(START.hour).minute(START.minute)),
        monday.hour(START.hour).minute(START.minute),
      ),
      createOption(
        buildLabel('Monday', monday.hour(LUNCH.hour).minute(LUNCH.minute)),
        monday.hour(LUNCH.hour).minute(LUNCH.minute),
      ),
      createOption(
        buildLabel('Tuesday', tuesday.hour(START.hour).minute(START.minute)),
        tuesday.hour(START.hour).minute(START.minute),
      ),
    ];
  }

  if (isFriday && phase === 'before') {
    return [
      createOption(buildLabel('This', today.hour(START.hour).minute(START.minute)), today.hour(START.hour).minute(START.minute)),
      createOption(buildLabel('This', today.hour(LUNCH.hour).minute(LUNCH.minute)), today.hour(LUNCH.hour).minute(LUNCH.minute)),
      createOption(
        buildLabel('Monday', nextBusinessDay(today, 1).hour(START.hour).minute(START.minute)),
        nextBusinessDay(today, 1).hour(START.hour).minute(START.minute),
      ),
    ];
  }

  if (isFriday && phase === 'morning') {
    return [
      createOption(buildLabel('This', today.hour(LUNCH.hour).minute(LUNCH.minute)), today.hour(LUNCH.hour).minute(LUNCH.minute)),
      createOption(
        buildLabel('Monday', nextBusinessDay(today, 1).hour(START.hour).minute(START.minute)),
        nextBusinessDay(today, 1).hour(START.hour).minute(START.minute),
      ),
      createOption(
        buildLabel('Monday', nextBusinessDay(today, 1).hour(LUNCH.hour).minute(LUNCH.minute)),
        nextBusinessDay(today, 1).hour(LUNCH.hour).minute(LUNCH.minute),
      ),
    ];
  }

  if (isThursdayAfterHours) {
    return [
      createOption(
        buildLabel('Tomorrow', tomorrow.hour(START.hour).minute(START.minute)),
        tomorrow.hour(START.hour).minute(START.minute),
      ),
      createOption(
        buildLabel('Tomorrow', tomorrow.hour(LUNCH.hour).minute(LUNCH.minute)),
        tomorrow.hour(LUNCH.hour).minute(LUNCH.minute),
      ),
      createOption(
        buildLabel('Monday', nextBusinessDay(tomorrow, 2).hour(START.hour).minute(START.minute)),
        nextBusinessDay(tomorrow, 2).hour(START.hour).minute(START.minute),
      ),
    ];
  }

  if (phase === 'before') {
    const startToday = today.hour(START.hour).minute(START.minute);
    const lunchToday = today.hour(LUNCH.hour).minute(0);
    const startTomorrow = tomorrow.hour(START.hour).minute(START.minute);

    options.push(createOption(buildLabel('This', startToday), startToday));
    options.push(createOption(buildLabel('This', lunchToday), lunchToday));
    options.push(createOption(buildLabel('Tomorrow', startTomorrow), startTomorrow));
    return options;
  } else if (phase === 'morning') {
    options.push(
      createOption(buildLabel('This', today.hour(LUNCH.hour).minute(LUNCH.minute)), today.hour(LUNCH.hour).minute(LUNCH.minute)),
    );
    options.push(
      createOption(
        buildLabel('Tomorrow', tomorrow.hour(START.hour).minute(START.minute)),
        tomorrow.hour(START.hour).minute(START.minute),
      ),
    );
    options.push(
      createOption(
        buildLabel('Tomorrow', tomorrow.hour(LUNCH.hour).minute(LUNCH.minute)),
        tomorrow.hour(LUNCH.hour).minute(LUNCH.minute),
      ),
    );
    return options;
  }

  if (isFriday && phase === 'afternoon') {
    const monday = nextBusinessDay(today, 1);
    const tuesday = nextBusinessDay(monday, 1);

    return [
      createOption(
        buildLabel('Monday', monday.hour(START.hour).minute(START.minute)),
        monday.hour(START.hour).minute(START.minute),
      ),
      createOption(
        buildLabel('Monday', monday.hour(LUNCH.hour).minute(LUNCH.minute)),
        monday.hour(LUNCH.hour).minute(LUNCH.minute),
      ),
      createOption(
        buildLabel('Tuesday', tuesday.hour(START.hour).minute(START.minute)),
        tuesday.hour(START.hour).minute(START.minute),
      ),
    ];
  }

  const nextDay = tomorrow;
  const thirdDay = nextBusinessDay(nextDay, 1);
  const thirdLabelPrefix = thirdDay.day() === 1 ? 'Monday' : thirdDay.format('dddd');

  options.push(
    createOption(
      buildLabel('Tomorrow', nextDay.hour(START.hour).minute(START.minute)),
      nextDay.hour(START.hour).minute(START.minute),
    ),
  );
  options.push(
    createOption(
      buildLabel('Tomorrow', nextDay.hour(LUNCH.hour).minute(LUNCH.minute)),
      nextDay.hour(LUNCH.hour).minute(LUNCH.minute),
    ),
  );
  options.push(
    createOption(
      buildLabel(thirdLabelPrefix, thirdDay.hour(START.hour).minute(START.minute)),
      thirdDay.hour(START.hour).minute(START.minute),
    ),
  );

  return options;
};

const getNextBusinessDay = (from: Dayjs): Dayjs => {
  let candidate = from.add(1, 'day');
  while (candidate.day() === 0 || candidate.day() === 6) {
    candidate = candidate.add(1, 'day');
  }
  return candidate.startOf('day');
};

const useAvailableSlots = (isToday: boolean, dateForDSTCheck: Dayjs) => {
  const toMinutes = (t: { hour: number; minute: number }) => t.hour * 60 + t.minute;
  const userTimeZone = useTimeZone();
  const now = dayjs();
  const { START, END } = getAdjustedBusinessHours(userTimeZone, dateForDSTCheck);

  const startMinutes = toMinutes(START);
  const endMinutes = toMinutes(END);
  const crossesMidnight = endMinutes < startMinutes;

  const DAY_MINUTES = 24 * 60;
  const TIME_INTERVAL = 15;

  const slots: { hour: number; minute: number }[] = [];

  const addSlots = (from: number, to: number) => {
    for (let m = from; m <= to; m += TIME_INTERVAL) {
      slots.push({ hour: Math.floor(m / 60), minute: m % 60 });
    }
  };

  if (crossesMidnight) {
    addSlots(startMinutes, DAY_MINUTES - 1);
    addSlots(0, endMinutes);
  } else {
    addSlots(startMinutes, endMinutes);
  }

  if (isToday && dateForDSTCheck.isSame(now, 'day')) {
    const nowMins = now.hour() * 60 + now.minute();
    return slots.filter(({ hour, minute }) => {
      const slotMins = hour * 60 + minute;
      return slotMins > nowMins;
    });
  }

  return slots;
};

const useAvailableMinutes = (selectedDate: Dayjs | null, selectedHour24: number) => {
  const userTimeZone = useTimeZone();
  const currentBusinessHours = getAdjustedBusinessHours(userTimeZone, selectedDate ?? dayjs());

  const now = dayjs();

  if (!selectedDate?.isSame(now, 'day')) {
    return TIME_INTERVALS;
  }

  if (selectedHour24 === now.hour()) {
    return TIME_INTERVALS.filter((m) => Number(m) > now.minute());
  }

  if (selectedHour24 > now.hour() && selectedHour24 <= currentBusinessHours.END.hour) {
    return TIME_INTERVALS;
  }

  return [];
};

const useTimeState = (visible: boolean, initialSendAt?: string) => {
  const [timeState, setTimeState] = useState<TimeState>({
    date: dayjs().format('MMM D, YYYY'),
    time: '8:00 AM',
    selectedDate: null,
  });

  const updateDate = useCallback((date: string) => {
    setTimeState((prev) => ({ ...prev, date }));
  }, []);

  const updateTime = useCallback((time: string) => {
    setTimeState((prev) => ({ ...prev, time }));
  }, []);

  const updateSelectedDate = useCallback((selectedDate: Dayjs | null) => {
    setTimeState((prev) => ({ ...prev, selectedDate }));
  }, []);

  useEffect(() => {
    if (!visible) return;

    const now = dayjs().second(0).millisecond(0);
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const businessHours = getAdjustedBusinessHours(userTimeZone, now);

    let base: Dayjs;

    const lastSlotToday = now.clone().hour(businessHours.END.hour).minute(businessHours.END.minute).second(0).millisecond(0);

    const noMoreTimeToday = now.isAfter(lastSlotToday);

    if (initialSendAt) {
      base = dayjs(initialSendAt);
    } else {
      base = (() => {
        const startOfDay = now.clone().hour(businessHours.START.hour).minute(businessHours.START.minute).second(0).millisecond(0);

        if (now.isBefore(startOfDay)) {
          return startOfDay;
        }

        const nextMinuteStr = TIME_INTERVALS.find((m) => Number(m) > now.minute());
        const minute = nextMinuteStr ? Number(nextMinuteStr) : 0;
        let hour = now.hour();
        if (!nextMinuteStr) {
          hour += 1;
        }

        const proposed = now.clone().hour(hour).minute(minute).second(0).millisecond(0);

        const afterEndOfDay =
          hour > businessHours.END.hour || (hour === businessHours.END.hour && minute > businessHours.END.minute);

        if (proposed.day() === 6 || proposed.day() === 0 || afterEndOfDay) {
          return getNextBusinessDay(now)
            .hour(businessHours.START.hour)
            .minute(businessHours.START.minute)
            .second(0)
            .millisecond(0);
        }

        return proposed;
      })();
    }

    const date = base.format('MMM D, YYYY');
    const time = base.format('h:mm A');

    setTimeState({
      date,
      time,
      selectedDate: base,
    });
  }, [visible, initialSendAt]);

  useEffect(() => {
    const parsedDate = dayjs(timeState.date, 'MMM D, YYYY', true);
    const parsedTime = dayjs(timeState.time, 'h:mm A', true);

    if (!parsedDate.isValid()) return;

    let updated = parsedDate;
    if (parsedTime.isValid()) {
      updated = updated.hour(parsedTime.hour()).minute(parsedTime.minute()).second(0).millisecond(0);
    } else {
      updated = updated.hour(1).minute(0).second(0).millisecond(0);
    }

    updateSelectedDate(updated);
  }, [timeState.date, timeState.time, updateSelectedDate]);

  return {
    ...timeState,
    updateDate,
    updateTime,
    updateSelectedDate,
  };
};

const ModalWrapper: React.FC<{
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
}> = ({ visible, onClose, children, width = MODAL_WIDTH }) => (
  <Modal
    className="scheduled-comment-modal custom-modal"
    visible={visible}
    onCancel={onClose}
    footer={null}
    style={{ top: 20 }}
    width="auto"
    wrapClassName="min-width-modal"
    centered
    modalRender={() => (
      <div className="custom-wrapper">
        <div className="ant-modal-content" style={{ borderRadius: '12px', padding: 0 }}>
          <button type="button" className="ant-modal-close">
            <span className="ant-modal-close-x" onClick={onClose}>
              <CloseOutlined />
            </span>
          </button>
          <div className="ant-modal-body" style={{ padding: 0, width }}>
            {children}
          </div>
        </div>
      </div>
    )}
  />
);

const QuickOptionsModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSubmit: (sendAt: string) => void;
  onPickDateTime: () => void;
}> = ({ visible, onClose, onSubmit, onPickDateTime }) => {
  const userTimeZone = useTimeZone();
  const quickOptions = useQuickOptions();

  const handleQuickSelect = useCallback(
    (date: Dayjs) => {
      const formatted = date.utc().format();
      onSubmit(formatted);
    },
    [onSubmit],
  );

  return (
    <ModalWrapper visible={visible} onClose={onClose}>
      <div>
        <div style={{ padding: '16px' }}>
          <Title level={3}>Schedule send</Title>
          <Text type="secondary">{userTimeZone}</Text>
        </div>

        <div style={{ width: '100%', gap: '8px', marginBottom: '16px' }}>
          {quickOptions.map((option) => (
            <Button
              key={option.label}
              type="text"
              block
              className={styles.customHoverButton}
              onClick={() => handleQuickSelect(option.value)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 16px',
                height: '40px',
              }}
            >
              <span>{option.label}</span>
              <span>{formatDateForDisplay(option.value)}</span>
            </Button>
          ))}
        </div>

        <div style={{ borderTop: '1px solid #d8d8d8' }}>
          <Button
            type="link"
            className={styles.customHoverButton}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'start',
              padding: '0 16px',
              height: '40px',
              gap: '8px',
            }}
            block
            onClick={onPickDateTime}
          >
            <CalendarOutlined />
            <span>Pick date & time</span>
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

const DateTimePickerModal: React.FC<{
  visible: boolean;
  onClose: () => void;
  onSubmit: (sendAt: string) => void;
  timeState: TimeState & {
    updateDate: (date: string) => void;
    updateTime: (time: string) => void;
    updateSelectedDate: (date: Dayjs | null) => void;
  };
}> = ({ visible, onClose, onSubmit, timeState }) => {
  const { date, time, selectedDate, updateDate, updateTime, updateSelectedDate } = timeState;
  const userTimeZone = useTimeZone();

  const isToday = dayjs(date, 'MMM D, YYYY').isSame(dayjs(), 'day');
  const currentSelectedDateForDSTCheck = selectedDate || dayjs();
  const currentBusinessHours = getAdjustedBusinessHours(userTimeZone, currentSelectedDateForDSTCheck);
  const selectedHour24 = dayjs(time, 'h:mm A').hour();

  const availableSlots = useAvailableSlots(isToday, currentSelectedDateForDSTCheck);
  const availableHours = Array.from(new Set(availableSlots.map((s) => s.hour)));
  const availableMinutes = useAvailableMinutes(selectedDate, selectedHour24);

  const handleCalendarSelect = useCallback(
    (value: Moment) => {
      const dayjsDate = dayjs(value.toDate()).startOf('day');
      const formattedDate = dayjsDate.format('MMM D, YYYY');
      updateDate(formattedDate);

      const now = dayjs();
      let selectedTime: Dayjs;

      const isToday = dayjsDate.isSame(now, 'day');

      const start = currentBusinessHours.START;
      const end = currentBusinessHours.END;

      const nowMinutes = now.hour() * 60 + now.minute();
      const startMinutes = start.hour * 60 + start.minute;
      const endMinutes = end.hour * 60 + end.minute;

      if (isToday) {
        if (nowMinutes < startMinutes) {
          selectedTime = dayjsDate.hour(start.hour).minute(start.minute).second(0).millisecond(0);
        } else if (nowMinutes > endMinutes) {
          const tomorrowMorning = dayjsDate.add(1, 'day').hour(start.hour).minute(start.minute).second(0).millisecond(0);
          selectedTime = tomorrowMorning;
          updateTime(selectedTime.format('h:mm A'));
          updateSelectedDate(selectedTime);
          return;
        } else {
          const nextSlotStr = TIME_INTERVALS.find((interval) => now.minute() < Number(interval));
          let minute = nextSlotStr ? Number(nextSlotStr) : 0;
          let hour = now.hour();

          if (!nextSlotStr) {
            hour += 1;
            minute = 0;
          }
          selectedTime = dayjsDate.hour(hour).minute(minute).second(0).millisecond(0);
        }
      } else {
        selectedTime = dayjsDate.hour(start.hour).minute(start.minute).second(0).millisecond(0);
      }

      updateTime(selectedTime.format('h:mm A'));
      updateSelectedDate(selectedTime);
    },
    [updateDate, updateTime, updateSelectedDate, currentBusinessHours],
  );

  const handleSchedule = useCallback(() => {
    const localDateTime = dayjs(`${date} ${time}`, 'MMM D, YYYY h:mm A', true);

    if (localDateTime.isValid()) {
      const utc = localDateTime.utc().second(0).millisecond(0);
      const formatted = utc.format('YYYY-MM-DDTHH:mm:ss');
      onSubmit(formatted);
    }
  }, [date, time, onSubmit]);

  const handleHourChange = useCallback(
    (selectedHour: number) => {
      let hour = Number(selectedHour);
      const selectedDateDayjs = dayjs(date, 'MMM D, YYYY', true);
      const now = dayjs();

      let minute = 0;
      if (selectedDateDayjs.isSame(now, 'day')) {
        if (hour === now.hour()) {
          const validMinute = TIME_INTERVALS.map((m) => Number(m)).find((m) => m > now.minute());
          minute = validMinute ?? now.add(1, 'minute').minute();
        } else if (hour < now.hour()) {
          hour = now.hour();
          minute = now.minute() + 1;
        }
      }

      const updated = selectedDateDayjs.hour(hour).minute(minute).second(0).millisecond(0);

      updateTime(updated.format('h:mm A'));
    },
    [date, updateTime],
  );

  const handleMinuteChange = useCallback(
    (selectedMinute: string) => {
      const minute = Number.parseInt(selectedMinute, 10);
      const dateTime = dayjs(`${date} ${time}`, 'MMM D, YYYY h:mm A');
      const updated = dateTime.minute(minute);
      updateTime(updated.format('h:mm A'));
    },
    [date, time, updateTime],
  );

  const currentHourDisplay = useMemo(() => {
    const parsed = dayjs(time, 'h:mm A', true);

    if (!parsed.isValid()) return 12;

    const currentHour24 = parsed.hour();

    if (currentHour24 === 0) return 12;
    if (currentHour24 > 12) return currentHour24 - 12;
    return currentHour24;
  }, [time]);

  const currentMinuteDisplay = useMemo(() => {
    return dayjs(time, 'h:mm A').format('mm');
  }, [time]);

  return (
    <ModalWrapper visible={visible} onClose={onClose} width={600}>
      <div className="ant-modal-body">
        <Title level={3}>Pick date & time</Title>

        <div style={{ display: 'flex', gap: 24 }}>
          <div className="site-calendar-customize-header-wrapper" style={{ maxWidth: CALENDAR_MAX_WIDTH }}>
            <Calendar
              value={selectedDate ? moment(selectedDate.format('YYYY-MM-DD')) : undefined}
              fullscreen={false}
              onSelect={handleCalendarSelect}
              disabledDate={(currentDate) => {
                const day = currentDate.day();
                const isWeekendDay = day === 0 || day === 6;
                const isBeforeTodayDate = currentDate.isBefore(moment().startOf('day'), 'day');

                const now = dayjs();
                const isToday = currentDate.isSame(moment(), 'day');
                const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const businessHours = getAdjustedBusinessHours(userTimeZone, now);

                const endHour = businessHours.END.hour;
                const endMinute = businessHours.END.minute;

                const lastAvailableSlot = dayjs()
                  .hour(endHour)
                  .minute(endMinute)
                  .second(0)
                  .millisecond(0)
                  .subtract(TIME_INTERVALS[0] ? Number(TIME_INTERVALS[0]) : 30, 'minute');

                const disableToday = (() => {
                  if (!isToday) return false;
                  return now.isAfter(lastAvailableSlot);
                })();

                return isWeekendDay || isBeforeTodayDate || disableToday;
              }}
              headerRender={({ value, onChange }) => {
                const year = value.year();
                const month = value.month();
                const localeData = value.localeData();

                const monthOptions = Array.from({ length: 12 }, (_, i) => {
                  const monthValue = value.clone().month(i);
                  return (
                    <Select.Option key={i} value={i} className="month-item">
                      {localeData.monthsShort(monthValue)}
                    </Select.Option>
                  );
                });

                const yearOptions = Array.from({ length: 20 }, (_, i) => {
                  const yearValue = year - 10 + i;
                  return (
                    <Select.Option key={yearValue} value={yearValue} className="year-item">
                      {yearValue}
                    </Select.Option>
                  );
                });

                return (
                  <div style={{ padding: 8 }}>
                    <Row gutter={8}>
                      <Col>
                        <Select
                          size="small"
                          dropdownMatchSelectWidth={false}
                          className="my-year-select"
                          value={year}
                          onChange={(newYear) => onChange(value.clone().year(newYear))}
                        >
                          {yearOptions}
                        </Select>
                      </Col>
                      <Col>
                        <Select
                          size="small"
                          dropdownMatchSelectWidth={false}
                          value={month}
                          onChange={(newMonth) => {
                            const next = value.clone().month(newMonth);
                            onChange(getFirstWeekdayOfMonth(next));
                          }}
                        >
                          {monthOptions}
                        </Select>
                      </Col>
                    </Row>
                  </div>
                );
              }}
            />
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <input
              type="text"
              value={date}
              onChange={(e) => updateDate(e.target.value)}
              placeholder="Jun 16, 2025"
              style={{
                padding: '8px 12px',
                fontSize: 16,
                borderRadius: 4,
                border: '1px solid #d9d9d9',
              }}
            />

            <div
              style={{
                display: 'flex',
                gap: 0,
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                overflow: 'hidden',
                alignItems: 'center',
              }}
            >
              <Select
                value={currentHourDisplay}
                onChange={handleHourChange}
                style={{
                  width: '33.33%',
                  borderRight: '1px solid #d9d9d9',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                bordered={false}
              >
                {availableHours.map((hour24) => {
                  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
                  return (
                    <Select.Option key={hour24} value={hour24}>
                      {hour12}
                    </Select.Option>
                  );
                })}
              </Select>

              <Select
                bordered={false}
                style={{
                  width: '33.33%',
                  borderRight: '1px solid #d9d9d9',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                value={currentMinuteDisplay}
                onChange={handleMinuteChange}
              >
                {availableMinutes.map((minute) => (
                  <Select.Option key={minute} value={minute}>
                    {minute}
                  </Select.Option>
                ))}
              </Select>

              <div
                style={{
                  width: '33.33%',
                  textAlign: 'center',
                  fontSize: 16,
                  background: '#fafafa',
                  height: 38,
                  lineHeight: '38px',
                }}
              >
                {selectedDate ? selectedDate.format('A') : 'AM'}
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" disabled={!date || !time} onClick={handleSchedule}>
            Schedule send
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};

export const CommentSchedule = ({ visible, onClose, initialSendAt, onSubmit }: Props) => {
  const [showPicker, setShowPicker] = useState(!!initialSendAt);
  const timeState = useTimeState(visible, initialSendAt);

  const handlePickDateTime = useCallback(() => {
    setShowPicker(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowPicker(false);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!visible) {
      setShowPicker(false);
    }
  }, [visible]);

  return (
    <>
      <QuickOptionsModal
        visible={visible && !showPicker}
        onClose={handleClose}
        onSubmit={onSubmit}
        onPickDateTime={handlePickDateTime}
      />

      <DateTimePickerModal visible={visible && showPicker} onClose={handleClose} onSubmit={onSubmit} timeState={timeState} />
    </>
  );
};

export const CommendScheduleError = ({ visible, onClose }: { visible: boolean; onClose: () => void }) => (
  <ModalWrapper visible={visible} onClose={onClose}>
    <div style={{ padding: '16px' }}>
      <div>
        <Title level={3}>Error</Title>
      </div>
      <div style={{ width: '100%', gap: '8px', marginBottom: '16px', padding: '0 12px' }}>
        <Text>Please write a comment before</Text>
      </div>
    </div>
  </ModalWrapper>
);
